import {
  getCurrencyFromStorage,
  isRubyOffer,
  isWebcoinOffer,
  isCashPack,
  isWebCoinItem,
  isRubyItem,
} from '@wwe-portal/web/utils/offer';
import { v4 } from 'uuid';
import {
  SysUIParameter,
  getDefaultMeta,
  sendGameTransactionEvent,
  sendSysPayment,
  sendSysUI,
} from './common';
import { addDDError } from './datadog';
import { getPageInfo } from './path-helper';
import { PurchasingOffer } from '@wwe-portal/web/services/payment/type';
import { OfferData, OfferItem } from '@wwe-portal/entities/offer.type';
import { sendGTMEvent } from './gtm-helper';
import { isFreeClaimOffer } from '@wwe-portal/web/components/stepup-offers/lib/mapper';
import {
  Payment,
  PaymentContext,
} from '@wwe-portal/web/providers/stores/payment-store';

const gtmCategory = 'Special Offers';

export interface SysGameTransactionParamter {
  context: unknown;
  item_balance: unknown;
  item_id: unknown;
  item_name: unknown;
  quantity: unknown;
  reference_id?: unknown;
  trans_desc: unknown;
  trans_type: unknown;
  transaction_id: unknown;
}

export const getItemQuantityByText = (offerData: OfferData) => {
  return (
    offerData.OfferItemTexts?.map((text) =>
      Number(text.replace('x', ''))
    ).reduce((sum: number, itemQuantity) => sum + itemQuantity, 0) ?? null
  );
};

export const getCurrencyRates = (
  currencies: {
    name: string;
    rate: number;
  }[]
) => {
  const webCoinCurrencyRate = currencies?.find(
    (el) => el.name === 'Web Coin'
  )?.rate;
  const rubyRate = currencies?.find((el) => el.name === 'Ruby')?.rate;
  return { webCoinCurrencyRate, rubyRate };
};

export const generateScreenContent = (
  offerId: string,
  offerItems: OfferItem[],
  position: number
) => {
  return JSON.stringify({
    offer_id: offerId,
    position,
    featured_item: offerItems?.map((item) => item.itemID),
  });
};

export const generateScreenContentV2 = (
  offerId: string,
  offerItems: OfferItem[],
  position: number
) => {
  return JSON.stringify({
    offer_id: offerId,
    offer_position_index: position,
    featured_item: offerItems?.map((item) => item.itemID),
  });
};

export const sendSysOfferTransactionSuccess = ({
  offerData,
  transactionId,

  webcoinBalance,
  rubyBalance,

  currentUser,
  currencies,
}: {
  offerData: OfferData;
  transactionId: string;

  webcoinBalance: number | undefined;
  rubyBalance: number | undefined;
  currentUser: { userId: string } | undefined;
  currencies: {
    name: string;
    rate: number;
  }[];
}) => {
  try {
    const { sys_platform, sys_os_s, reference_id } = getDefaultMeta();

    const { webCoinCurrencyRate, rubyRate } = getCurrencyRates(currencies);

    const initialParameters = {
      sys_user_id: currentUser?.userId ?? '',
      sys_platform,
      sys_os_s,
      currentUser,
      transaction_type: 'earn',
      trans_type: 'web_portal_iap',
      event_source: 'web_portal',
      transaction_id: transactionId.length ? transactionId : v4(),
      context: offerData?.OffersID ?? null,
      reference_id,
    };

    offerData.analyticsItems?.forEach((item) => {
      let item_balance: number | null = null;
      if (item.itemName) {
        if (isWebCoinItem(item.itemName)) {
          item_balance = webcoinBalance ?? null;
        } else if (isRubyItem(item.itemName)) {
          item_balance = rubyBalance ?? null;
        }
      }

      sendGameTransactionEvent({
        ...initialParameters,
        trans_desc: 'earn_item',
        item_balance,
        item_id: item?.shardID ?? item.itemID ?? null,
        item_name: item.itemName ?? null,
        quantity: Number(item.quantity),
        reference_subtype_3: null,
      });
    });

    if (!isWebcoinOffer(offerData) && webCoinCurrencyRate) {
      sendGameTransactionEvent({
        ...initialParameters,
        trans_desc: 'earn_currency_exchange_reward',
        item_id: 'web_currency_web_coin',
        item_name: 'web_coin',
        quantity: Math.round(
          +(offerData?.Price?.USD ?? 0) * webCoinCurrencyRate
        ),
        item_balance: webcoinBalance,
        reference_subtype_3: null,
      });
    }

    if (!isRubyOffer(offerData) && rubyRate) {
      sendGameTransactionEvent({
        ...initialParameters,
        trans_desc: 'earn_currency_exchange_reward',
        item_id: 'web_currency_ruby',
        item_name: 'ruby',
        quantity: Math.round(+(offerData?.Price?.USD ?? 0) * rubyRate),
        item_balance: rubyBalance,
        reference_subtype_3: null,
      });
    }

    if (isCashPack(offerData)) {
      sendGameTransactionEvent({
        ...initialParameters,
        trans_desc: 'earn_item',
        item_id: 'HardCurrency',
        item_name: 'Cash',
        quantity: getItemQuantityByText(offerData),
        item_balance: null,
        reference_subtype_3: null,
      });
    }
  } catch (e) {
    addDDError(e);
  }
};

export const sendSysCloseTransaction = ({
  offerData,
  analyticsData,
  category,
}: {
  offerData: OfferData;
  analyticsData: Partial<SysUIParameter> | undefined;
  category?: string;
}) => {
  const screen_category = getCurrencyFromStorage(
    sessionStorage.getItem('offerCategory')
  );

  sendSysUI({
    flow_name: 'web_store_telemetric',
    screen_content: generateScreenContent(
      offerData.OffersID,
      offerData.offerItems ?? [],
      0
    ),
    interaction_object_category: null,
    flow_order: null,
    screen_category: category ?? screen_category ?? 'special_offers',
    screen_subcategory: 'payment_success',
    screen_type: 'pop-up',
    ...analyticsData,
    interaction_object: 'close_button',
    interaction_type: 'load',
  });
};

export const sendSysRewardTransaction = ({
  offerData,
  transactionId,
  currentUser,
}: {
  offerData: OfferData;
  transactionId: string;
  currentUser: { userId: string } | undefined;
}) => {
  try {
    const { sys_platform, sys_os_s, reference_id } = getDefaultMeta();

    offerData.analyticsItems?.forEach((item) => {
      sendGameTransactionEvent({
        trans_type: 'reward_web_portal',
        trans_desc: 'earn_free_reward',
        context: offerData.OffersID ?? null,
        reference_id,
        event_source: 'web_portal',
        transaction_id: transactionId.length ? transactionId : v4(),
        sys_user_id: currentUser?.userId ?? '',
        sys_platform,
        sys_os_s,
        item_id: item.shardID ?? item.itemID ?? null,
        item_name: item.itemName ?? null,
        quantity: Number(item.quantity ?? 0),
        item_balance: null,
        currentUser,
        transaction_type: 'earn',
        reference_subtype_3: null,
      });
    });
  } catch (e) {
    addDDError(e);
  }
};

export const sendOfferPaymentEvent = ({
  offer,
  paymentId,
  transactionId,
  success,
  error,
  currentUser,
  screenId,
}: {
  offer: OfferData | null;
  paymentId?: string;
  transactionId?: string;
  success: boolean;
  error?: string;
  currentUser: undefined | { userId: string };
  screenId?: number;
}) => {
  const pageInfo = getPageInfo(window.location.pathname);
  const hasRegionalPricing =
    offer?.regionalPrice &&
    offer.regionalPrice.raw.amount &&
    offer.regionalPrice.raw.amountNoDiscount;
  const defaultCurrency = offer?.defaultCurrency ?? 'USD';
  const currency = hasRegionalPricing
    ? offer.regionalPrice!.currency
    : defaultCurrency;
  const amountLocal = hasRegionalPricing
    ? Number.parseFloat(offer.regionalPrice!.raw.amount)
    : undefined;
  const originalAmountLocal = hasRegionalPricing
    ? Number.parseFloat(offer.regionalPrice!.raw.amountNoDiscount)
    : undefined;
  sendSysPayment({
    success,
    error,
    tenant: currentUser?.userId,
    context: pageInfo ? `${screenId || pageInfo.id}|${pageInfo.pageName}` : '',
    amount_us:
      offer?.InitialPrice?.[defaultCurrency] ??
      offer?.Price?.[defaultCurrency] ??
      0,
    amount_local: amountLocal ?? offer?.Price?.[defaultCurrency] ?? 0,
    local_currency_type: currency,
    special: offer?.OfferDisplayID,
    special_id: offer?.OffersID,
    store_sku: offer?.XsollaID,
    game_sku: offer?.HardCurrencyPackageID,
    payment_id: paymentId,
    transaction_id: transactionId?.length ? transactionId : v4(),
    sku_type: offer?.OfferType ?? 'Standard',
    is_discounted_package: !!offer?.DiscountPercentage,
    discount_info: {
      discount_id: '',
      original_amount_local: originalAmountLocal ?? offer?.StrikeThroughPrice,
      original_amount_us: offer?.StrikeThroughPrice,
    },
    section_title: offer?.StoreItemTagline,
    section_id: offer?.OfferSectionID,
    section_weight: null,
    section_color: '',
    offer_weight: offer?.OfferWeight,
  });
};

export const paymentScreenIsLoaded = (
  offerId: string,
  screen_category: string | null
) => {
  console.log('paymentScreenIsLoaded');
  sendSysUI({
    flow_name: 'web_store_telemetric',
    interaction_type: 'load',
    interaction_object: null,
    screen_content: offerId,
    interaction_object_category: null,
    flow_order: null,
    screen_category,
    screen_subcategory: 'payment',
    screen_type: 'pop-up',
  });
};

export const paymentScreenIsClosed = (
  purchasingOffer: PurchasingOffer | null | undefined
) => {
  if (!purchasingOffer) return;
  sendSysCloseTransaction({
    offerData: purchasingOffer?.offerData,
    analyticsData: purchasingOffer.analyticsData,
  });

  sendGTMEvent({
    category: 'Xsolla Popup Closed',
    action: `xsolla_popup/${purchasingOffer.offerData.StoreItemName}/clicked_close`,
    event: 'xsolla_popup_closed',
  });
};

export const specialOfferPageIsLoaded = () => {
  sendSysUI({
    flow_name: 'web_store_telemetric',
    interaction_type: 'load',
    interaction_object: null,
    screen_content: '',
    interaction_object_category: null,
    flow_order: null,
    screen_category: 'special_offers',
    screen_subcategory: null,
    screen_type: 'screen',
  });
};

export const specialOfferPageIsArrival = () => {
  sendSysUI({
    flow_name: 'web_store_arrival',
    interaction_type: 'load',
    interaction_object: null,
    screen_content: '',
    interaction_object_category: null,
    flow_order: null,
    screen_category: 'special_offers',
    screen_subcategory: null,
    screen_type: 'screen',
  });
};

export const specialOfferPageIsScrolling = (screen_category: string | null) => {
  sendSysUI({
    flow_name: 'web_store_telemetric',
    interaction_type: 'scroll',
    interaction_object: null,
    screen_content: '',
    screen_category,
    interaction_object_category: null,
    flow_order: null,
    screen_subcategory: null,
    screen_type: 'screen',
  });

  sendGTMEvent({
    category: gtmCategory,
    event: `special_offers_section_viewed`,
    action: `special-offers/${screen_category}/viewed`,
  });
};

export const specialOfferSectionIsScrolling = (
  screen_category: string | null,
  offerList: any[]
) => {
  offerList.map((offer) => {
    sendSysUI({
      flow_name: 'web_store_telemetric',
      interaction_type: 'impression',
      interaction_object: null,
      screen_content: generateScreenContentV2(
        offer.offerId,
        offer.offerItems,
        offer.position
      ),
      screen_category,
      interaction_object_category: null,
      flow_order: null,
      screen_subcategory: null,
      screen_type: 'screen',
    });
  });
};

export const specialOfferDialogShowed = (offer: {
  offerId: string;
  offerItems: OfferItem[];
  position: number;
}) => {
  sendSysUI({
    flow_name: 'web_store_telemetric',
    interaction_type: 'tap',
    interaction_object: 'more_info_button',
    screen_content: generateScreenContent(
      offer.offerId,
      offer.offerItems,
      offer.position
    ),
    interaction_object_category: 'user_action',
    flow_order: null,
    screen_subcategory: null,
    screen_type: 'popup',
  });
};

export const specialOfferDialogClosed = (offer: {
  offerId: string;
  offerItems: OfferItem[];
  position: number;
}) => {
  sendSysUI({
    flow_name: 'web_store_telemetric',
    interaction_type: 'tap',
    interaction_object: 'close_button',
    screen_content: generateScreenContent(
      offer.offerId,
      offer.offerItems,
      offer.position
    ),
    interaction_object_category: 'user_action',
    flow_order: null,
    screen_subcategory: null,
    screen_type: 'popup',
  });
};

export const sendRewardsAnalyticsEvent = (
  purchasingOffer: PurchasingOffer | null | undefined,
  { currentUser }: Pick<PaymentContext, 'currentUser'>
) => {
  if (!purchasingOffer) return;
  sendSysRewardTransaction({
    offerData: purchasingOffer?.offerData,
    transactionId: purchasingOffer?.transactionId ?? '',
    currentUser,
  });

  sendGTMEvent({
    category: gtmCategory,
    action: `special-offers/${purchasingOffer.offerData.StoreItemName}/claimed`,
    event: 'special_offers_claimed',
  });
};

export const sendPurchaseFailedEvent = (
  offerData: OfferData,
  { currentUser }: Pick<PaymentContext, 'currentUser'>,
  error?: unknown
) => {
  sendOfferPaymentEvent({
    success: false,
    offer: offerData,
    transactionId: undefined,
    paymentId: undefined,
    currentUser,
    error: error?.['message'] ?? 'Unknown Error',
  });
};

export const sendOfferPurchaseEvent = async (
  payment: Payment,
  { currencies, balance, currentUser, screenId }: PaymentContext
) => {
  const transactionId = payment.paymentId ?? v4();

  sendSysOfferTransactionSuccess({
    offerData: payment.offerData,
    transactionId,
    webcoinBalance: balance?.webCoin,
    rubyBalance: balance?.ruby,
    currencies,
    currentUser,
  });

  sendOfferPaymentEvent({
    success: true,
    offer: payment.offerData,
    transactionId,
    paymentId: payment.paymentId,
    currentUser,
    screenId,
  });

  sendGTMEvent({
    category: gtmCategory,
    action: `special-offers/${payment.offerData.StoreItemName}/purchased`,
    event: 'special_offers_purchased',
  });
};

interface OfferButtonIsTappedProps {
  overriddenParameter?: Partial<SysUIParameter>;
  offerData: OfferData;
}

const sendOfferButtonClickedGTMEvent = (offerData: OfferData) => {
  if (isFreeClaimOffer(offerData)) {
    sendGTMEvent({
      category: gtmCategory,
      action: `special-offers/${offerData.StoreItemName}/clicked_claim`,
      event: 'special_offers_clicked_claim',
    });
  } else {
    sendGTMEvent({
      category: gtmCategory,
      action: `special-offers/${offerData.StoreItemName}/clicked_purchase`,
      event: 'special_offers_clicked_purchased',
    });
  }
};

// export const sendOfferButtonIsTappedEvent = ({
//   offerData,
//   overriddenParameter,
// }: OfferButtonIsTappedProps) => {
//   sendSysUI({
//     flow_name: 'web_store_telemetric',
//     interaction_type: 'tap',
//     interaction_object: 'buy_button',
//     screen_content: offerData.OffersID,
//     interaction_object_category: null,
//     flow_order: null,
//     // screen_category: getCurrencyFromStorage(offerType) || 'special_offers',
//     screen_subcategory: 'offer',
//     screen_type: 'screen',
//     ...overriddenParameter,
//   });

//   sendOfferButtonClickedGTMEvent(offerData);
// };

export const sendOfferButtonIsTappedEvent = ({
  offerData,
  overriddenParameter,
  position,
}: OfferButtonIsTappedProps & { position: number }) => {
  sendSysUI({
    flow_name: 'web_store_telemetric',
    interaction_type: 'tap',
    interaction_object: 'buy_button',
    screen_content: JSON.stringify({
      offer_id: offerData.OffersID,
      position,
      featured_item: offerData.offerItems?.map((item) => item.itemName),
    }),
    interaction_object_category: null,
    flow_order: null,
    // screen_category: getCurrencyFromStorage(offerType) || 'special_offers',
    screen_subcategory: 'offer',
    ...overriddenParameter,
    screen_type: 'pop-up',
  });

  sendOfferButtonClickedGTMEvent(offerData);
};

export const sendFlagshipOfferButtonIsTappedEvent = ({
  offerData,
  slideIndex,
  totalSlides,
}: OfferButtonIsTappedProps & { slideIndex: number; totalSlides: number }) => {
  sendSysUI({
    flow_name: 'web_store_telemetric',
    interaction_type: 'tap',
    interaction_object: 'buy_button',
    interaction_object_category: null,
    flow_order: null,
    screen_category: 'special_offers',
    screen_subcategory: 'offer_banner',
    screen_type: 'pop-up',
    screen_content: JSON.stringify({
      offer_id: offerData.OffersID,
      position: slideIndex,
      total_banner_slots: totalSlides,
    }),
  });

  sendOfferButtonClickedGTMEvent(offerData);
};

export const sendFlagshipOfferSlideEvent = ({
  previousOfferId,
  currentOfferId,
  previousIndex,
  currentIndex,
  totalSlots,
}: {
  previousOfferId: string | undefined;
  currentOfferId: string | undefined;
  previousIndex: number;
  currentIndex: number;
  totalSlots: number;
}) => {
  sendSysUI({
    screen_type: 'screen',
    screen_category: 'special_offers',
    screen_subcategory: 'offer_banner',
    screen_content: JSON.stringify({
      offer_id: previousOfferId,
      position: previousIndex + 1,
      total_banner_slots: totalSlots,
      go_to_offer_id: currentOfferId,
      go_to_position: currentIndex + 1,
    }),
    previous_screen_id: null,
    previous_screen_name: null,
    interaction_type: 'tap',
    interaction_object:
      currentIndex > previousIndex
        ? 'go_to_next_button'
        : 'go_to_previous_button',
    interaction_object_category: null,
    flow_name: 'web_store_telemetric',
    flow_order: null,
  });
};

export const paymentPopupIsSuccess = (offerId: string) => {
  console.log('paymentPopupIsSuccess', offerId);
  sendSysUI({
    flow_name: 'web_store_telemetric',
    interaction_type: 'load',
    interaction_object: null,
    screen_content: offerId,
    interaction_object_category: null,
    flow_order: null,
    screen_category:
      getCurrencyFromStorage(sessionStorage.getItem('offerCategory')) ??
      'special_offers',
    screen_subcategory: 'payment_success',
    screen_type: 'pop_up',
  });
};

export const sendOfferClickEvent = (name: string) => {
  console.log('sendOfferClickEvent', name);
  const event = {
    category: gtmCategory,
    event: 'special_offers_clicked_info_icon',
    action: `special-offers/${name}/clicked_info`,
  };

  sendGTMEvent(event);
};
